import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Crown } from 'lucide-react';
import { ccySym } from '@/utils/currencyUtils';
import '@/styles/commission-preview.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;

export const RULE_LABELS = {
  PRO_SUBSCRIBED: 'abonné PRO',
  FIRST_MISSION: '1re mission',
  ESCROW: 'séquestre',
  STANDARD: 'taux standard',
};

/** Repère de couleur des commissions réduites (le taux standard n'en a pas). */
const RULE_CHIPS = {
  PRO_SUBSCRIBED: { label: 'PRO', cls: 'cmp-chip--pro' },
  FIRST_MISSION: { label: '1re mission', cls: 'cmp-chip--reduit' },
  ESCROW: { label: 'séquestre', cls: 'cmp-chip--reduit' },
};

const fmt = (n, currency) => {
  const zeroDec = ['XOF', 'XAF', 'NGN', 'GHS'].includes((currency || 'EUR').toUpperCase());
  return `${Number(n).toLocaleString('fr-FR', { minimumFractionDigits: zeroDec ? 0 : 2, maximumFractionDigits: zeroDec ? 0 : 2 })} ${ccySym(currency || 'EUR')}`;
};

/**
 * Pour le transporteur, avant d'accepter ou de proposer : sa commission (taux et raison) et le total qu'il recevra —
 * même forme que l'encadré des frais de l'expéditeur (`SenderFeeSummary`). Tout vient de
 * `GET /missions/commission-preview`, y compris la comparaison avec PRO (`pro_offer`, `pro_saving`) : l'écran ne
 * calcule aucune commission. Promotion de l'abonnement pour un non-abonné, gain affiché pour un abonné.
 */
export default function CommissionPreview({ amount, currency = 'EUR', paymentReserved = false, totalLabel = "Total à recevoir s'il accepte" }) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!(Number(amount) > 0)) { setPreview(null); return undefined; }
    let cancel = false;
    axios.get(`${API}/missions/commission-preview`, {
      params: { amount: Number(amount), currency, payment_reserved: paymentReserved },
    })
      .then(({ data }) => { if (!cancel) setPreview(data); })
      .catch(() => { if (!cancel) setPreview(null); });
    return () => { cancel = true; };
  }, [amount, currency, paymentReserved]);

  if (!preview) return null;
  const ccy = preview.currency;
  const pct = Math.round(preview.commission_rate * 100);
  const minApplied = preview.platform_commission > preview.commission_rate * Number(amount) + 0.005;
  const chip = RULE_CHIPS[preview.commission_rule];
  const offer = preview.pro_offer;

  return (
    <div className="cmp" data-testid="commission-preview">
      <div className="cmp-row">
        <span>Montant de la proposition</span>
        <span>{fmt(preview.amount ?? amount, ccy)}</span>
      </div>
      <div className="cmp-row">
        <span>
          Commission Colis2Ship
          {chip && <span className={`cmp-chip ${chip.cls}`}>{chip.label}</span>}
          <span className="cmp-sub" data-testid="commission-preview-rule">
            {pct} % · {RULE_LABELS[preview.commission_rule] || preview.commission_rule}
            {minApplied ? ` · minimum ${fmt(preview.commission_min, ccy)}` : ''}
          </span>
        </span>
        <span data-testid="commission-preview-amount">− {fmt(preview.platform_commission, ccy)}</span>
      </div>
      <div className="cmp-row cmp-total">
        <span>{totalLabel}</span>
        <span data-testid="commission-preview-net">{fmt(preview.net, ccy)}</span>
      </div>
      {offer && (
        <a className="cmp-promo" href="/app/abonnement" target="_blank" rel="noopener noreferrer" data-testid="commission-preview-upsell">
          <span className="cmp-promo-line">
            <Crown size={14} aria-hidden="true" />
            <span>
              Avec PRO, {Math.round(offer.commission_rate * 100)} % au lieu de {pct} % : vous recevriez{' '}
              <strong>{fmt(offer.net, ccy)}</strong>{' '}<span className="cmp-nowrap">(+ {fmt(offer.gain, ccy)})</span>
            </span>
          </span>
          <span className="cmp-promo-sub">
            {offer.trial_available
              ? `${offer.trial_days} jours d'essai gratuit, puis ${fmt(offer.monthly_eur, 'EUR')}/mois`
              : `Abonnement ${fmt(offer.monthly_eur, 'EUR')}/mois`}
            {' · '}<span className="cmp-promo-link">Découvrir PRO</span>
          </span>
        </a>
      )}
      {preview.pro_saving > 0 && (
        <div className="cmp-saving" data-testid="commission-preview-saving">
          <Check size={14} aria-hidden="true" /> PRO vous fait gagner {fmt(preview.pro_saving, ccy)} sur cette mission
        </div>
      )}
    </div>
  );
}
